import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss'],
  providers: [],
})

export class AudioPlayerComponent implements AfterViewInit, OnDestroy {

    @Input() audioSrc: string = '';
  
    @Input() set volume(vol: number) {
        this._volume = Math.min(1, Math.max(0, vol / 100));
        if (this.audioElement?.nativeElement) {
            this.audioElement.nativeElement.volume = this._volume;
        }
    }
    get volume(): number {
        return this._volume * 100;
    }
    
    @Input() set speed(rate: number) {
        this._speed = Math.min(2.0, Math.max(0.25, rate));
        if (this.audioElement?.nativeElement) {
            this.audioElement.nativeElement.playbackRate = this._speed;
        }
    }
    get speed(): number {
        return this._speed;
    }
    
    @ViewChild('audioElement') audioElement!: ElementRef<HTMLAudioElement>;
    isPlaying: boolean = false;
    currentTime: number = 0;
    duration: number = 0;
    progressPercent: number = 0;
    private animationFrameId: number | null = null;
    private _volume: number = 0.7; // Default to 70%
    private _speed: number = 1.0; // Default to normal speed

    ngAfterViewInit() {
        this.setupAudioEvents();
        // Initialize volume and speed
        const audio = this.audioElement.nativeElement;
        audio.volume = this._volume;
        audio.playbackRate = this._speed;
    }

    ngOnDestroy() {
        this.cleanUpAnimationFrame();
    }

    private setupAudioEvents() {
        const audio = this.audioElement.nativeElement;
        audio.addEventListener('play', () => {
            this.isPlaying = true;
            this.startProgressAnimation();
        });
        audio.addEventListener('pause', () => {
            this.isPlaying = false;
            this.cleanUpAnimationFrame();
        });
        audio.addEventListener('ended', () => this.onAudioEnd());
    }

    private startProgressAnimation() {
        this.cleanUpAnimationFrame();
        
        const updateProgress = () => {
            if (!this.isPlaying) return;
            
            const audio = this.audioElement.nativeElement;
            this.currentTime = audio.currentTime;
            this.progressPercent = (this.currentTime / this.duration) * 100;
            
            this.animationFrameId = requestAnimationFrame(updateProgress);
        };
        
        this.animationFrameId = requestAnimationFrame(updateProgress);
    }

    private cleanUpAnimationFrame() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    togglePlay() {
        const audio = this.audioElement.nativeElement;
        if (this.isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
    }

    setDuration() {
        this.duration = this.audioElement.nativeElement.duration;
    }

    onAudioEnd() {
        this.isPlaying = false;
        this.audioElement.nativeElement.currentTime = 0;
        this.currentTime = 0;
        this.progressPercent = 0;
        this.cleanUpAnimationFrame();
    }

    seek(event: MouseEvent) {
        const progressContainer = event.currentTarget as HTMLElement;
        const rect = progressContainer.getBoundingClientRect();
        const clickPosition = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
        const seekPercentage = clickPosition / rect.width;
        
        const audio = this.audioElement.nativeElement;
        audio.currentTime = this.duration * seekPercentage;
        this.progressPercent = seekPercentage * 100;
        this.currentTime = audio.currentTime;
        
        if (!this.isPlaying) {
            audio.play();
        }
    }

}
